import { FC, useRef } from "react";
import classNames from "classnames";
import { useNavigate } from "react-router-dom";
import { useUserSettings } from "../../context/userSettingsContext";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import useToggle from "../../hooks/useToggle";
import { useI18n } from "../../hooks/useTranslations";
import Notifications from "../notifications/Notifications";
import SvgNotifications from "../icons/Notifications";
import Icon from "./Icon";
import styles from "./NotificationBell.module.css";

const NotificationBell: FC = () => {
  const i18n = useI18n();
  const navigate = useNavigate();
  const { isOpen, close, toggle } = useToggle();
  const notificationBellRef = useRef<HTMLDivElement>(null);
  const userSettings = useUserSettings();
  const notifications = userSettings.notifications;

  const dueDateNotifications =
    (notifications?.dueDateExpired ?? 0) > 0
      ? (notifications?.dueDateExpired ?? 0)
      : (notifications?.dueDateExpiringSoon ?? 0);

  const notificationCount =
    (userSettings.permissions.carPurchases.read
      ? (notifications?.carPurchaseExpired ?? 0) + (notifications?.carPurchaseArrivingSoon ?? 0)
      : 0) +
    (userSettings.permissions.users.read ? (notifications?.usersMissingCostCenterCount ?? 0) : 0) +
    (userSettings.permissions.carTasks.read ? (notifications?.taskToDo ?? 0) : 0) +
    (userSettings.permissions.dueDates.read ? dueDateNotifications : 0) +
    (userSettings.permissions.carLeasings.read
      ? (notifications?.carLeasingExpired ?? 0) + (notifications?.carLeasingExpiringSoon ?? 0)
      : 0);
  const hasErrors =
    (userSettings.permissions.carPurchases.read && (notifications?.carPurchaseExpired ?? 0) > 0) ||
    (userSettings.permissions.users.read && (notifications?.usersMissingCostCenterCount ?? 0) > 0) ||
    (userSettings.permissions.carTasks.read && (notifications?.taskToDo ?? 0) > 0) ||
    (userSettings.permissions.dueDates.read && (notifications?.dueDateExpired ?? 0) > 0) ||
    (userSettings.permissions.carLeasings.read && (notifications?.carLeasingExpired ?? 0) > 0);

  useOnClickOutside(notificationBellRef, () => isOpen && close());

  return (
    <div ref={notificationBellRef} className={styles.container}>
      <button
        type="button"
        className={classNames(styles.button, { [styles.open]: isOpen })}
        aria-label={i18n.t.common.notifications}
        aria-expanded={isOpen}
        title={i18n.t.common.notifications}
        onClick={toggle}
      >
        <Icon glyph={SvgNotifications} className={styles.icon} />
        {notificationCount > 0 && (
          <span className={classNames(styles.badge, { [styles.error]: hasErrors })}>{notificationCount}</span>
        )}
      </button>
      <div className={classNames(styles.dropdown, { [styles.visible]: isOpen })}>
        <Notifications
          onSelect={(to) => {
            navigate(to);
            close();
          }}
        />
      </div>
    </div>
  );
};

export default NotificationBell;
